"use client";

import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useRef, useEffect, useCallback, memo, useState } from "react";
import {
  ScrollArea,
  Button,
  Input,
  Combobox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "visuais";
import { Trash2, Plus, PieChart, Eraser } from "lucide-react";
import { useRateio } from "@PN/inclusao";
import { toast } from "react-hot-toast";
import { useCatalogs } from "store";
import { useDebounce } from "hooks";
import { usePrenotaStore } from "@PN/inclusao";

// Variantes de animação coordenadas com o Dialog - otimizadas para 120fps
const tableVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
  },
};

const headerVariants = {
  hidden: { opacity: 0, y: -20, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
  },
};

const emptyStateVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
  },
};

const rateioItemVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.95,
  },
};

export const RateioTable = memo(function RateioTable() {
  const tableRef = useRef<HTMLDivElement>(null);
  const { centroCusto } = useCatalogs();
  const shouldReduceMotion = useReducedMotion();
  const {
    rateios,
    calculations,
    temRegrasEspeciais,
    filiaisOptions,
    addRateio,
    removeRateio,
    clearAllRateios,
    editRateio,
    processExcel,
    modoImportacao,
    setModoImportacao,
  } = useRateio();

  // Para acessar draft diretamente e evitar stale closures
  const draft = usePrenotaStore((state) => state.draft);

  // ✅ CTRL+V FUNCIONA - Handler para paste de dados do Excel
  const handleExcelPasteRef = useRef<(clipboardData: string) => Promise<void> | null>(null);

  // Atualiza a referência sempre que processExcel muda
  useEffect(() => {
    handleExcelPasteRef.current = processExcel;
  }, [processExcel]);

  const handleExcelPaste = useCallback(
    async (e: ClipboardEvent) => {
      // ✅ Verifica se o foco está dentro da tabela ou se é um paste global
      const target = e.target as Element;
      const isWithinTable = tableRef.current?.contains(target);
      const isFocusedInput =
        target?.tagName === "INPUT" || target?.tagName === "TEXTAREA";

      // Se está digitando em um input que não é da tabela, ignora
      if (isFocusedInput && !isWithinTable) {
        return;
      }

      // Se não está na tabela e não é um paste "global", ignora
      if (!isWithinTable && document.activeElement !== document.body) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      try {
        const clipboardData = e.clipboardData?.getData("text") || "";

        if (!clipboardData.trim()) {
          toast.error("Clipboard está vazio");
          return;
        }

        console.log(
          "🔄 Processando dados do Excel:",
          clipboardData.substring(0, 100) + "..."
        );

        // Usa a referência atual para evitar stale closures
        await handleExcelPasteRef.current?.(clipboardData);
      } catch (error) {
        console.error("❌ Erro ao processar paste:", error);
        toast.error("Erro ao processar os dados colados");
      }
    },
    [] // ✅ Sem dependências - função estável
  );

  // Event listener para paste - otimizado
  useEffect(() => {
    const tableElement = tableRef.current;
    if (tableElement) {
      // ✅ Adiciona no document com capture=true para pegar antes de outros handlers
      const pasteHandler = (e: Event) => handleExcelPaste(e as ClipboardEvent);

      // Handlers para indicar quando pode colar
      const handleMouseEnter = () => {
        setCanPaste(true);
      };
      const handleMouseLeave = () => setCanPaste(false);
      const handleFocusIn = () => setCanPaste(true);
      const handleFocusOut = (e: FocusEvent) => {
        // Só remove o indicador se o foco saiu completamente da tabela
        if (!tableElement.contains(e.relatedTarget as Node)) {
          setCanPaste(false);
        }
      };

      document.addEventListener("paste", pasteHandler, { capture: true });
      tableElement.addEventListener("mouseenter", handleMouseEnter);
      tableElement.addEventListener("mouseleave", handleMouseLeave);
      tableElement.addEventListener("focusin", handleFocusIn);
      tableElement.addEventListener("focusout", handleFocusOut);

      return () => {
        document.removeEventListener("paste", pasteHandler, { capture: true });
        tableElement.removeEventListener("mouseenter", handleMouseEnter);
        tableElement.removeEventListener("mouseleave", handleMouseLeave);
        tableElement.removeEventListener("focusin", handleFocusIn);
        tableElement.removeEventListener("focusout", handleFocusOut);
      };
    }
  }, [handleExcelPaste]); // ✅ Removido rateios.length das dependências

  // Estado para nova linha vazia
  const [newRowData, setNewRowData] = useState({
    filial: "",
    centroCusto: "",
    valor: "",
    percentual: "",
  });

  // Estado para indicar quando pode colar
  const [canPaste, setCanPaste] = useState(false);

  // ✅ Debounce para inputs de valor e percentual
  const debouncedValor = useDebounce(newRowData.valor, 300);
  const debouncedPercentual = useDebounce(newRowData.percentual, 300);

  // Função para adicionar nova linha
  const handleAddNewRow = () => {
    if (
      !newRowData.filial ||
      !newRowData.centroCusto ||
      (!temRegrasEspeciais && !newRowData.valor && !newRowData.percentual)
    ) {
      toast.error("Preencha todos os campos");
      return;
    }

    let valor = 0;
    let percentual = 0;

    if (temRegrasEspeciais) {
      // Para tipos com regras especiais, sempre usa o valor total da NF
      valor = calculations.totalGeral;
      percentual = 100;
    } else {
      if (newRowData.valor) {
        valor = parseFloat(newRowData.valor.replace(",", "."));
        percentual =
          calculations.totalGeral > 0
            ? (valor / calculations.totalGeral) * 100
            : 0;
      } else if (newRowData.percentual) {
        percentual = parseFloat(newRowData.percentual.replace(",", "."));
        valor =
          calculations.totalGeral > 0
            ? (percentual / 100) * calculations.totalGeral
            : 0;
      }

      if (valor <= 0) {
        toast.error("Valor deve ser maior que zero");
        return;
      }
    }

    addRateio({
      filial: newRowData.filial,
      centroCusto: newRowData.centroCusto,
      valor,
      percentual,
    });

    // Limpa a nova linha
    setNewRowData({ filial: "", centroCusto: "", valor: "", percentual: "" });
  };

  const getCentroCustoOptions = (filial: string) => {
    const options = centroCusto.getOptionsByFilial?.(filial) ?? [];
    return [...options].sort((a, b) =>
      a.value.localeCompare(b.value, "pt-BR", { sensitivity: "base" })
    );
  };

  // ✅ Função para editar rateio com debounce
  const handleRateioChange = useCallback(
    (index: number, field: string, value: string) => {
      const rateio = rateios[index];
      const newData = { ...rateio };

      if (field === "filial") {
        newData.filial = value;
        newData.centroCusto = ""; // Limpa CC quando muda filial
        editRateio(index, newData); // ✅ Salva imediatamente para filial
      } else if (field === "centroCusto") {
        newData.centroCusto = value;
        editRateio(index, newData); // ✅ Salva imediatamente para centro de custo
      } else if (field === "valor") {
        // ✅ Para valor e percentual, usar debounce
        const valor = parseFloat(value.replace(",", "."));
        if (!isNaN(valor)) {
          newData.valor = valor;
          newData.percentual =
            calculations.totalGeral > 0
              ? (valor / calculations.totalGeral) * 100
              : 0;
          editRateio(index, newData);
        }
      } else if (field === "percentual") {
        const percentual = parseFloat(value.replace(",", "."));
        if (!isNaN(percentual)) {
          newData.percentual = percentual;
          newData.valor =
            calculations.totalGeral > 0
              ? (percentual / 100) * calculations.totalGeral
              : 0;
          editRateio(index, newData);
        }
      }
    },
    [rateios, calculations.totalGeral, editRateio]
  );

  // =====================================================
  // MODO NORMAL - Tabela editável
  // =====================================================
  return (
    <motion.div
      ref={tableRef}
      variants={shouldReduceMotion ? undefined : tableVariants}
      initial={shouldReduceMotion ? undefined : "hidden"}
      animate="visible"
      className={`flex h-full flex-col overflow-hidden rounded-xl border bg-gradient-to-br from-background to-muted/20 shadow-lg transition-all duration-200 ${
        canPaste ? "ring-2 ring-blue-500/50 shadow-blue-500/20" : ""
      }`}
      style={{ willChange: "transform, opacity" }}
    >
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4">
          {/* Barra de ações - só mostra quando há rateios */}
          {rateios.length > 0 && (
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <PieChart className="h-3.5 w-3.5" />
                  <strong>{rateios.length}</strong> {rateios.length === 1 ? 'rateio' : 'rateios'}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={clearAllRateios}
                className="h-7 gap-1.5 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Eraser className="h-3.5 w-3.5" />
                Limpar Tudo
              </Button>
            </div>
          )}
          
          {/* Sempre mostra a tabela com pelo menos uma linha para adicionar */}
          <div className="min-w-[620px]">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[120px] min-w-[120px]">
                    Filial
                  </TableHead>
                  <TableHead className="w-[200px] min-w-[200px]">
                    Centro de Custo
                  </TableHead>
                  <TableHead className="w-[120px] min-w-[120px] text-right">
                    Valor
                  </TableHead>
                  <TableHead className="w-[100px] min-w-[100px] text-right">
                    %
                  </TableHead>
                  <TableHead className="w-[80px] min-w-[80px] text-center">
                    Ações
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Rateios existentes */}
                <AnimatePresence>
                  {rateios.map((rateio, index) => (
                    <motion.tr
                      key={index}
                      layout={!shouldReduceMotion}
                      variants={
                        shouldReduceMotion ? undefined : rateioItemVariants
                      }
                      custom={index}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      whileHover={
                        shouldReduceMotion
                          ? {}
                          : {
                              backgroundColor: "rgba(var(--primary), 0.05)",
                            }
                      }
                      className="hover:bg-primary/5 transition-colors"
                    >
                      <TableCell>
                        <Combobox
                          options={filiaisOptions}
                          value={rateio.filial}
                          onChange={(value: string | undefined) =>
                            handleRateioChange(index, "filial", value || "")
                          }
                          placeholder="Selecione"
                        />
                      </TableCell>
                      <TableCell>
                        <Combobox
                          options={getCentroCustoOptions(rateio.filial)}
                          value={rateio.centroCusto}
                          onChange={(value: string | undefined) =>
                            handleRateioChange(
                              index,
                              "centroCusto",
                              value || ""
                            )
                          }
                          placeholder="Selecione"
                          disabled={!rateio.filial}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="text"
                          value={
                            temRegrasEspeciais
                              ? `R$ ${rateio.valor
                                  .toFixed(2)
                                  .replace(".", ",")}`
                              : rateio.valor.toFixed(2).replace(".", ",")
                          }
                          onChange={(e) =>
                            !temRegrasEspeciais &&
                            handleRateioChange(index, "valor", e.target.value)
                          }
                          placeholder={temRegrasEspeciais ? "Total NF" : "0,00"}
                          className="text-right font-mono h-8"
                          disabled={temRegrasEspeciais}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="text"
                          value={
                            temRegrasEspeciais
                              ? "100,0%"
                              : rateio.percentual.toFixed(2).replace(".", ",")
                          }
                          onChange={(e) =>
                            !temRegrasEspeciais &&
                            handleRateioChange(
                              index,
                              "percentual",
                              e.target.value
                            )
                          }
                          placeholder={temRegrasEspeciais ? "100%" : "0,00"}
                          className="text-right font-mono h-8"
                          disabled={temRegrasEspeciais}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => removeRateio(index)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </motion.tr>
                  ))}
                </AnimatePresence>

                {/* Linha para adicionar novo rateio - sempre visível */}
                <TableRow className="border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10">
                  <TableCell>
                    <Combobox
                      options={filiaisOptions}
                      value={newRowData.filial}
                      onChange={(value: string | undefined) =>
                        setNewRowData((prev) => ({
                          ...prev,
                          filial: value || "",
                          centroCusto: "",
                        }))
                      }
                      placeholder="Selecione filial..."
                    />
                  </TableCell>
                  <TableCell>
                    <Combobox
                      options={getCentroCustoOptions(newRowData.filial)}
                      value={newRowData.centroCusto}
                      onChange={(value: string | undefined) =>
                        setNewRowData((prev) => ({
                          ...prev,
                          centroCusto: value || "",
                        }))
                      }
                      placeholder="Selecione CC..."
                      disabled={!newRowData.filial}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="text"
                      value={
                        temRegrasEspeciais
                          ? `R$ ${calculations.totalGeral
                              .toFixed(2)
                              .replace(".", ",")}`
                          : newRowData.valor
                      }
                      onChange={(e) =>
                        !temRegrasEspeciais &&
                        setNewRowData((prev) => ({
                          ...prev,
                          valor: e.target.value,
                          percentual: "", // Limpa percentual quando digita valor
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddNewRow();
                        }
                      }}
                      placeholder={
                        temRegrasEspeciais ? "Total NF" : "Digite valor..."
                      }
                      className="text-right font-mono h-8"
                      disabled={temRegrasEspeciais}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="text"
                      value={
                        temRegrasEspeciais ? "100,0%" : newRowData.percentual
                      }
                      onChange={(e) =>
                        !temRegrasEspeciais &&
                        setNewRowData((prev) => ({
                          ...prev,
                          percentual: e.target.value,
                          valor: "", // Limpa valor quando digita percentual
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddNewRow();
                        }
                      }}
                      placeholder={temRegrasEspeciais ? "100%" : "Ou %..."}
                      className="text-right font-mono h-8"
                      disabled={temRegrasEspeciais}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleAddNewRow}
                      className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                      disabled={
                        !newRowData.filial ||
                        !newRowData.centroCusto ||
                        (!temRegrasEspeciais &&
                          !newRowData.valor &&
                          !newRowData.percentual)
                      }
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {/* Dicas de uso - só mostra quando não há rateios */}
          {rateios.length === 0 && (
            <motion.div
              variants={shouldReduceMotion ? undefined : emptyStateVariants}
              initial={shouldReduceMotion ? undefined : "hidden"}
              animate="visible"
              className="mt-6 p-4 bg-muted/30 rounded-lg border border-dashed border-muted-foreground/30"
              style={{ willChange: "transform, opacity" }}
            >
              <div className="text-sm text-muted-foreground space-y-3">
                <motion.p
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="font-medium text-foreground flex items-center gap-2"
                >
                  <PieChart className="h-4 w-4" />
                  💡 Dicas para rateio:
                </motion.p>
                
                {/* Select para modo de importação */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25 }}
                  className="ml-6 flex items-center gap-2"
                >
                  <span className="text-xs font-medium text-foreground">
                    📊 Modo de importação:
                  </span>
                  <Select
                    value={modoImportacao}
                    onValueChange={(value: 'valor' | 'percentual') => setModoImportacao(value)}
                  >
                    <SelectTrigger className="w-[180px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="valor">Valor (R$)</SelectItem>
                      <SelectItem value="percentual">Percentual (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="ml-6 space-y-1"
                >
                  <p className="text-xs">
                    📋 <strong>Ctrl+V:</strong> Cole do Excel:{" "}
                    <code className="bg-muted px-1 rounded">
                      Filial | CC | {modoImportacao === 'valor' ? 'Valor' : 'Percentual'}
                    </code>
                  </p>
                  <p className="text-xs">
                    ✅ <strong>Exemplo:</strong>{" "}
                    <code className="bg-muted px-1 rounded">
                      {modoImportacao === 'valor' ? '109 52 4,18' : '109 52 25,5'}
                    </code>
                  </p>
                  <p className="text-xs text-green-600">
                    ✅ Aceita códigos: 0101, 109, 09, 9
                  </p>
                  <p className="text-xs text-blue-600">
                    💡 <strong>Dica:</strong> Selecione as células no Excel e
                    pressione Ctrl+C, depois Ctrl+V aqui
                  </p>
                </motion.div>
              </div>
            </motion.div>
          )}
        </div>
      </ScrollArea>
    </motion.div>
  );
});
